import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, Loader2, Tag, Wrench, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Service } from "../../types/service";

interface GeneralServiceSelectProps {
  /** The general service the form was seeded from, if any. */
  value: Service | null;
  onSelect: (service: Service) => void;
  /**
   * Drops the pick. On the create form that means no code chosen yet; on the
   * edit form it means back to the code the rate is saved under — which is why
   * the cross only appears once something has actually been picked.
   */
  onClear?: () => void;
  /**
   * Searches the General (partnerId = null) side of the Services directory.
   * Injected rather than dispatched from here on purpose: the listing page
   * renders from the same redux slice, so a thunk would blank the table behind
   * the modal every time someone typed in this box.
   */
  fetchGeneralServices?: (params: {
    search: string;
    page: number;
    limit: number;
  }) => Promise<{ services: Service[] }>;
  disabled?: boolean;
  error?: boolean;
  placeholder: string;
  /**
   * The code the rate already carries, shown until a different one is picked.
   * Editing a client rate starts with no `value` — a rate stores its code, not
   * a pointer to the general service it came from — so without this the box
   * would look empty on a row that plainly has a code.
   */
  currentLabel?: string;
}

/**
 * Picks the general service a client-specific rate is copied from.
 *
 * Built to read as the same control as the client box above it — the directory
 * picker from the container order form — because the two are answering one
 * question together: whose rate, and for which code.
 *
 * Unlike that box this one opens with the list already loaded and does not wait
 * for two characters: a company's rate card is a short, finite set of codes, and
 * someone adding a client price usually wants to see what is on offer rather
 * than recall a code from memory.
 */
export const GeneralServiceSelect: React.FC<GeneralServiceSelectProps> = ({
  value,
  onSelect,
  onClear,
  fetchGeneralServices,
  disabled,
  error = false,
  placeholder,
  currentLabel,
}) => {
  const { t } = useTranslation(["services", "common"]);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doFetch = useCallback(
    async (q: string) => {
      if (!fetchGeneralServices) return;
      setLoading(true);
      setFailed(false);
      try {
        const res = await fetchGeneralServices({
          search: q,
          page: 1,
          limit: 100,
        });
        setServices(res.services || []);
      } catch {
        setServices([]);
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [fetchGeneralServices],
  );

  // Load on open, then debounce while typing.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doFetch(inputValue.trim()), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, inputValue, doFetch]);

  const close = useCallback(() => {
    setOpen(false);
    setInputValue("");
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [close]);

  const selectedLabel = value
    ? `${value.serviceId} · ${value.name}`
    : currentLabel || "";
  // The box shows the search term only while open, so closing it discards
  // whatever was typed and repaints whatever is actually picked.
  const displayValue = open ? inputValue : selectedLabel;

  const priceLabel = (service: Service) =>
    service.type === "MATRIX"
      ? service.type
      : service.price != null
        ? `${service.type} · €${service.price.toFixed(2)}`
        : service.type;

  const choose = (service: Service) => {
    onSelect(service);
    close();
    inputRef.current?.blur();
  };

  const sizeCls = "h-8 px-2.5 py-1 text-[13px]";

  if (!fetchGeneralServices || disabled) {
    return (
      <div
        className={`flex w-full items-center truncate rounded-md border border-[rgba(11,15,14,0.15)] bg-bone text-mist ${sizeCls}`}
      >
        {selectedLabel || "—"}
      </div>
    );
  }

  // One border-colour per state rather than layering — compiled-CSS ordering,
  // not className ordering, decides which of two border classes wins.
  const boxState = error
    ? `border-signal ${open ? "ring-2 ring-signal/40" : ""}`
    : open
      ? "border-transparent ring-2 ring-copper"
      : "border-[rgba(11,15,14,0.15)]";

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`flex min-w-0 items-center rounded-md border bg-white ${boxState}`}
      >
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          // Focusing does not fire again on a box that is already focused —
          // which it is after a pick — so clicking it has to open too.
          onClick={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              close();
            }
            if (e.key === "ArrowDown") setOpen(true);
          }}
          placeholder={placeholder}
          title={selectedLabel || placeholder}
          autoComplete="off"
          className={`min-w-0 flex-1 bg-transparent text-ink outline-none placeholder-mist ${sizeCls}`}
        />

        <div className="flex shrink-0 items-center gap-0.5 pr-1.5">
          {open && loading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-mist" />
          )}
          {/* Only a real pick can be dropped: on the edit form the box shows
              the saved code until one is made, and there is no clearing that. */}
          {!!value && !!onClear && !(open && loading) && (
            <button
              type="button"
              // Keeps focus in the input so the box does not blur-close
              // between mousedown and click.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onClear();
                close();
              }}
              title={t("common:buttons.clear")}
              className="rounded p-0.5 text-mist hover:text-signal"
              tabIndex={-1}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => (open ? close() : setOpen(true))}
            className="rounded p-0.5 text-mist hover:text-ink"
            tabIndex={-1}
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </button>
        </div>
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-[rgba(11,15,14,0.12)] bg-white shadow-xl">
          <div className="max-h-60 overflow-y-auto">
            {loading && services.length === 0 ? (
              <p className="flex items-center justify-center gap-2 py-5 text-[12px] text-mist">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t("services:form.searching")}
              </p>
            ) : failed ? (
              <p className="py-5 text-center text-[12px] text-signal">
                {t("services:form.generalServicesLoadFailed")}
              </p>
            ) : services.length === 0 ? (
              <p className="py-5 text-center text-[12px] text-mist">
                {inputValue.trim()
                  ? t("services:form.noGeneralServiceMatch", {
                      query: inputValue.trim(),
                    })
                  : t("services:form.noGeneralServicesYet")}
              </p>
            ) : (
              services.map((service) => (
                <button
                  key={service._id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(service)}
                  title={service.description || service.name}
                  className={`block w-full border-b border-[rgba(11,15,14,0.06)] px-3 py-2 text-left last:border-b-0 hover:bg-bone ${
                    value?._id === service._id ? "bg-copper/10" : ""
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <Wrench className="h-3.5 w-3.5 shrink-0 text-mist" />
                    <span className="truncate text-[13px] font-medium text-ink">
                      {service.name}
                    </span>
                    <span className="shrink-0 text-[11px] tabular-nums text-mist">
                      ({service.serviceId})
                    </span>
                  </span>
                  <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-mist">
                    <Tag className="h-3 w-3 shrink-0" />
                    <span className="truncate tabular-nums">
                      {priceLabel(service)}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
